import { useEffect, useState, useCallback } from "react";
import { Terminal, Plus, LogOut, Users, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import StatsBar from "@/components/StatsBar";
import SubAccountList from "@/components/SubAccountList";
import SubAccountDialog from "@/components/SubAccountDialog";
import SubAccountDetail from "@/components/SubAccountDetail";
import UsersDialog from "@/components/UsersDialog";
import SettingsDialog from "@/components/SettingsDialog";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const isAdmin = user?.role === "admin";

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch {
      /* ignore */
    }
  }, []);

  const loadSubs = useCallback(async () => {
    const { data } = await api.get("/subaccounts");
    setSubs(data);
    setSelectedId((cur) => cur || (data[0]?.id ?? null));
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadSubs();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [loadSubs]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sq = p.get("square");
    if (sq) {
      if (sq === "connected") toast.success("Square connected successfully");
      else if (sq === "denied") toast.error("Square connection was cancelled");
      else toast.error("Square connection failed");
      window.history.replaceState({}, "", window.location.pathname);
      loadSubs();
    }
  }, [loadSubs]);

  const selected = subs.find((s) => s.id === selectedId) || null;

  const upsertSub = (updated) => {
    setSubs((prev) => {
      const exists = prev.some((s) => s.id === updated.id);
      return exists ? prev.map((s) => (s.id === updated.id ? updated : s)) : [updated, ...prev];
    });
    setSelectedId(updated.id);
    loadStats();
  };

  const removeSub = (id) => {
    setSubs((prev) => prev.filter((s) => s.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
    loadStats();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-20 bg-background/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Terminal className="h-4 w-4 text-primary" />
            </div>
            <span className="font-heading font-bold">PayGate Console</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${isAdmin ? "bg-primary/15 text-primary border-primary/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`}>
              {isAdmin ? "ADMIN" : "OWNER"}
            </span>
            <span className="hidden sm:block text-xs font-mono text-muted-foreground">{user?.email}</span>
            {isAdmin && (
              <Button variant="outline" size="sm" data-testid="settings-btn" onClick={() => setSettingsOpen(true)} className="gap-1.5">
                <Globe className="h-4 w-4" />
                <span className={`text-[10px] font-mono font-bold ${settings?.mode === "production" ? "text-emerald-400" : "text-amber-400"}`}>
                  {settings?.mode === "production" ? "PROD" : "TEST"}
                </span>
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" data-testid="team-btn" onClick={() => setUsersOpen(true)} className="gap-1.5">
                <Users className="h-4 w-4" /> Team & Owners
              </Button>
            )}
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={logout} className="gap-1.5">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6 space-y-6">
        <StatsBar stats={stats} />

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          <aside className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold">{isAdmin ? "Sub-Accounts" : "My Account"}</h2>
              {isAdmin && (
                <Button size="sm" data-testid="new-subaccount-btn" onClick={() => setDialogOpen(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" /> New
                </Button>
              )}
            </div>
            <SubAccountList subs={subs} selectedId={selectedId} onSelect={setSelectedId} />
          </aside>

          <section>
            {selected ? (
              <SubAccountDetail sub={selected} onChange={upsertSub} onDelete={removeSub} isAdmin={isAdmin} baseUrl={settings?.active_base_url} />
            ) : (
              <div className="h-full min-h-[300px] flex items-center justify-center border border-dashed border-border rounded-lg text-muted-foreground text-sm">
                Select or create a sub-account to configure payments.
              </div>
            )}
          </section>
        </div>
      </main>

      <SubAccountDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={upsertSub} />
      <UsersDialog open={usersOpen} onOpenChange={setUsersOpen} subs={subs} />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={settings} onSaved={setSettings} />
    </div>
  );
}
